import React, { useRef, useState, useEffect } from 'react';
import './WebDev.css';
import DecryptedText from '../blocks/DecryptedText/DecryptedText';
import MetaBalls from '../blocks/MetaBalls/MetaBalls';
import SpotlightCard from '../blocks/SpotlightCard/SpotlightCard';
import ClickSpark from '../blocks/ClickSpark/ClickSpark';
import GooeyNav from '../blocks/GooeyNav/GooeyNav';

type WebProjectType = 'dream' | 'phpApplication' | 'newCat' | 'hogborts';

const WebDev: React.FC = () => {
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeProject, setActiveProject] = useState<WebProjectType>('dream');
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Back to top visibility logic
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const navItems = [
    { label: "Dream", href: "#dream" },
    { label: "PHP Application", href: "#phpApplication" },
    { label: "NewCat", href: "#newCat" },
    { label: "Hogborts", href: "#hogborts" },
  ];

  const handleNavSelect = (index: number) => {
    const projectTypes: WebProjectType[] = ['dream', 'phpApplication', 'newCat', 'hogborts'];
    setActiveProject(projectTypes[index]);
  };

  const getProjectImage = (): string => {
    switch(activeProject) {
      case 'dream':
        return '/images/projects/dream/showCase1.png';
      case 'phpApplication':
        return '/images/projects/webdev/php.jpg';
      case 'newCat':
        return '/images/projects/webdev/newcat.jpg';
      case 'hogborts':
        return '/images/projects/webdev/hogborts.jpg';
      default:
        return '/images/projects/dream/showCase1.png';
    }
  };

  const getProjectDescription = (): string => {
    switch(activeProject) {
      case 'dream':
        return "Interactive dream journal web application that allows users to record and visualize their dreams with unique animations and transitions.";
      case 'phpApplication':
        return "Collection of PHP applications demonstrating backend functionality, data storage solutions, and server-side rendering techniques.";
      case 'newCat':
        return "Website for a cat adoption center, featuring an archive system and contact forms. Designed to showcase both functionality and appealing aesthetics for pet adoption.";
      case 'hogborts':
        return "A front-end mockup of a personal Harry Potter wiki. This project showcases UI design skills and interactive elements for displaying fictional content.";
      default:
        return "";
    }
  };

  return (
<ClickSpark
                shape="heart"
                sparkColor='#BD2735'
                sparkSize={8}
                sparkRadius={40}
                sparkCount={16}
                duration={500}
              >      <div className="project-page webdev-page">
        <div className="home-link">
          <div className="metaball-wrapper">
            <MetaBalls 
              color="#266867" 
              enableTransparency={true}
              ballCount={8}
              animationSize={25}
              speed={0.2}
              cursorBallColor="#266867"
            />
          </div>
          <a href="/">Home</a>
        </div>

        <header className="project-header">
          <div className="container">
            <h1>
              <DecryptedText 
                text="WEB DESIGN"
                animateOn="view"
                sequential={true}
                maxIterations={4}
                speed={60}
                className="decrypted-title"
                parentClassName="title-container"
                encryptedClassName="encrypted-title"
              />
            </h1>
            <hr />
            <h2>
              <DecryptedText 
                text="JavaScript and PHP Work"
                animateOn="view"
                sequential={true}
                maxIterations={1}
                speed={20}
                className="decrypted-description"
                parentClassName="description-container"
                encryptedClassName="encrypted-description"
              />
            </h2>
          </div>
        </header>

        <div className="gooey-nav-wrapper">
          <GooeyNav 
            items={navItems}
            initialActiveIndex={navItems.findIndex(item => item.href === `#${activeProject}`)}
            animationTime={600}
            particleCount={15}
            particleDistances={[90, 10]}
            particleR={100}
            timeVariance={300}
            colors={[1, 2, 3, 1, 2, 3, 1, 4]}
            onItemSelect={(_, index: number) => handleNavSelect(index)}
          />
        </div>

        <main className="project-content">
          <div className="container">
            {/* Project Introduction */}
            <SpotlightCard 
              className="project-section introduction-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <div className="project-description">
                <p>This is the HOMEPAGE for my JavaScript and PHP work. It mainly demonstrates animations, data storage, and transitions in web applications. Feel free to explore through the navbar.</p>
              </div>
            </SpotlightCard>

            {/* Project Description Card */}
            <SpotlightCard 
              className="project-section description-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <div className="project-description">
                <p>{getProjectDescription()}</p>
              </div>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section project-showcase" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <div className="project-image-container">
                <img 
                  src={getProjectImage()} 
                  alt={`${activeProject} project showcase`}
                  className="project-image"
                />
              </div>
            </SpotlightCard>
          </div>
        </main>

        {/* Back to Top Button */}
        <div 
          className={`back-to-top ${showBackToTop ? 'visible' : ''}`}
          onClick={scrollToTop}
          aria-label="Back to top"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
          </svg>
        </div>
      </div>
    </ClickSpark>
  );
};

export default WebDev; 